import * as _ from 'underscore';

import { LongRunningAction } from '~permissions/services/LongRunningAction';
import { ILongRunningAction } from '~permissions/services/types/ILongRunningAction';
import { WebClientListResourceFetcher } from '~src/commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { IDataSourceBase } from '~src/datasource/models/IDataSourceInfo';
import { GetDataConnectionIdsFromDatasourcesRequest } from '~src/datasource/ServerApi/requests/GetDataConnectionIdsFromDatasourcesRequest';
import { GetDatasourceActionsRequest } from '~src/datasource/ServerApi/requests/GetDatasourceActionsRequest';
import { DatasourceIdToConnectionIdsMap } from '~src/datasource/ServerApi/types/DatasourceIdToConnectionIdsMap';
import { IDatasourceActions } from '~src/datasource/ServerApi/types/IDatasourceActions';
import { IListFetcher } from '~src/ts/fetchers/IListFetcher';
import { Filter } from '~src/ts/helpers/Filter';
import { ServerApiMapper } from '~src/ts/helpers/ServerModelHelper';
import { SliceableHelper } from '~src/ts/helpers/SliceableHelper';
import { ConnectionDetailInfo, isRemote } from '~src/ts/models/ConnectionDetailInfo';
import { IDataConnectionInfo } from '~src/ts/models/IDataConnectionInfo';
import { Set } from '~src/ts/models/Set';
import { FieldValuesQuery } from '~src/ts/resources/FieldValuesQuery';
import { IActionResource, IFieldValuesResource, IListResource } from '~src/ts/resources/Resource';
import { ResourceQuery } from '~src/ts/resources/ResourceQuery';
import { CheckConnectionRequest, ICheckConnectionParams, ICheckConnectionResult } from '~src/ts/ServerApi/requests/CheckConnectionRequest';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '~src/ts/ServerApi/requests/GeneratePublicKeyRequest';
import { GetDataConnectionsRequest } from '~src/ts/ServerApi/requests/GetDataConnectionsRequest';
import { GetFieldValuesRequest } from '~src/ts/ServerApi/requests/GetFieldValuesRequest';
import { UpdateConnectionsOAuthRequest } from '~src/ts/ServerApi/requests/UpdateConnectionsOAuthRequest';
import { IUpdateConnectionsParams, UpdateConnectionsRequest } from '~src/ts/ServerApi/requests/UpdateConnectionsRequest';
import { IFieldValue } from '~src/ts/ServerApi/types/IFieldValue';
import { FilterFieldNames } from '~src/ts/ServerApi/types/IFilterClause';

import { Messages } from '../../codegen/Localize';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';

export interface IEmbeddedPasswordUpdate {
  isEmbedded: boolean;
  newPassword?: string;
}

// Represents the intended updates to a data connection. All fields are optional.
export interface IConnectionUpdate {
  server?: string;
  port?: string;
  username?: string;
  passwordUpdate?: IEmbeddedPasswordUpdate;
  usingRemoteQueryAgent?: boolean;
}

class DataConnectionsResource implements IListResource<IDataConnectionInfo>, IFieldValuesResource, IActionResource<IDatasourceActions> {

  public getDisplayUsername(dataconnection: IDataConnectionInfo): string {
    const details: ConnectionDetailInfo = dataconnection.connectionDetails;
    let text = '';
    if (details && isRemote(details)) {
      text = details.databaseUsername;
    }
    return text;
  }

  public getDisplayAuthentication(dataconnection: IDataConnectionInfo): string {
    const details: ConnectionDetailInfo = dataconnection.connectionDetails;
    let text = '';
    if (details) {
      if (isRemote(details)) {
        if (details.hasEmbeddedPassword) {
          text = Messages.hasPasswordEmbedded();
        } else {
          text = Messages.hasPasswordNotEmbedded();
        }
      } else if (details.type === 'file') {
        text = Messages.none();
      }
    }
    return text;
  }

  private listFetcher: IListFetcher<IDataConnectionInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor() {
    this.listFetcher = new WebClientListResourceFetcher(GetDataConnectionsRequest, ServerApiMapper.dataConnections);
    this.fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public all(): ResourceQuery<IDataConnectionInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getConnectionIdsFromDatasourceIds(datasourceIds: string[]): Promise<DatasourceIdToConnectionIdsMap> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(datasourceIds, (idsBlock) =>
        new GetDataConnectionIdsFromDatasourcesRequest(idsBlock)
      ),
      (results: DatasourceIdToConnectionIdsMap[]) => {
        return Object.assign({}, ...results);
      });
  }

  public async getConnections(datasource: IDataSourceBase){
    const dataConnection = await this.getFromDatasource(datasource);
    return dataConnection;
 }


  public getFromDatasource(datasource: IDataSourceBase): Promise<IDataConnectionInfo[]> {
    return this.getFromDatasources(Set.singleton(datasource, ds => ds.id));
  }

  public getFromDatasourceByIds(datasourceIds: string[]): Promise<IDataConnectionInfo[]> {
    const makeQueryFromDatasourceIdsBlock = (idsBlock: string[]) =>
        this.all().addFilter(Filter.stringFieldInArray(FilterFieldNames.datasourceId, idsBlock));

    return SliceableHelper.fetchAllForSliceableWithArrayParam(datasourceIds, makeQueryFromDatasourceIdsBlock);
  }

  public getFromDatasources(datasources: Set<IDataSourceBase>): Promise<IDataConnectionInfo[]> {
    const datasourceIds = datasources.map(ds => ds.id);
    return this.getFromDatasourceByIds(datasourceIds);
  }

  public getForFlow(flowId: string): Promise<IDataConnectionInfo[]> {
    const query = this.all().addFilter(Filter.stringFieldEq(FilterFieldNames.flowId, flowId));
    return SliceableHelper.fetchAll(query).then(listResult => listResult.items);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'dataConnection');
  }

  public actions(datasourceId: string): Promise<IDatasourceActions> {
    // Since actions/ownership are defined at a data source level, we get the available actions
    // for the connection's data source and the UI shows only those that are relevant to connections.
    // TODO: Figure out how to refactor this to get the action on the datasource ID instead of the connection ID.
    // It'll work for now, but will break when the unified datasources go away on the backend.
    return WebHttpClient.sendRequest(new GetDatasourceActionsRequest(datasourceId));
  }

  public checkExistingConnection(dataSourceId: string): Promise<ICheckConnectionResult> {
    const request = new CheckConnectionRequest({ id: dataSourceId });
    return WebHttpClientWithErrorToastInstance.sendRequest(request);
  }

  public checkConnection(dataSourceId: string, connectionUpdate: IConnectionUpdate): Promise<ICheckConnectionResult> {

    const requestParams: ICheckConnectionParams =
    {
      id: dataSourceId,
      server: connectionUpdate.server,
      port: connectionUpdate.port,
      username: connectionUpdate.username
    };

    return DataConnections.setPasswordParams(requestParams, connectionUpdate.passwordUpdate).then(() => {
      return WebHttpClientWithErrorToastInstance.sendRequest(new CheckConnectionRequest(requestParams));
    });
  }

  public updateConnections(dataSourceIds: string[], connectionUpdate: IConnectionUpdate): Promise<IResult> {

    const requestParams: IUpdateConnectionsParams =
    {
      ids: dataSourceIds,
      server: connectionUpdate.server,
      port: connectionUpdate.port,
      username: connectionUpdate.username,
      usingRemoteQueryAgent: connectionUpdate.usingRemoteQueryAgent
    };

    return DataConnections.setPasswordParams(requestParams, connectionUpdate.passwordUpdate)
      .then(() => WebHttpClientWithErrorToastInstance.sendRequest(new UpdateConnectionsRequest(requestParams)))
      .then(LongRunningAction.getActionIdFromActionResult)
      .then((actionId: string) => LongRunningAction.trackAsyncJobProgressWithPromise(actionId))
      .then((longRunningAction: ILongRunningAction) => LongRunningAction.getActionResult<IResult>(longRunningAction));
  }

  public updateConnectionsOAuth(dataSourceIds: string[], keychainEntryId?: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequest(new UpdateConnectionsOAuthRequest({
        ids: dataSourceIds,
        keychainEntryId: keychainEntryId
      }))
      .then(LongRunningAction.getActionIdFromActionResult)
      .then((actionId: string) => LongRunningAction.trackAsyncJobProgressWithPromise(actionId))
      .then((longRunningAction: ILongRunningAction) => LongRunningAction.getActionResult<IResult>(longRunningAction));
  }

  private setPasswordParams(
      requestParams: { embedPassword?: boolean, encryptedPassword?: string, encryptedPasswordKeyId?: string },
      passwordUpdate: IEmbeddedPasswordUpdate): Promise<any> {

    if (passwordUpdate) {

      requestParams.embedPassword = passwordUpdate.isEmbedded;

      if (_.isString(passwordUpdate.newPassword)) {
        return WebHttpClient.sendRequest(new GeneratePublicKeyRequest()).
          then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
            requestParams.encryptedPassword = rsa.encrypt(passwordUpdate.newPassword, generatePublicKeyResult.key);
            requestParams.encryptedPasswordKeyId = generatePublicKeyResult.keyId;
          });
      }
    }

    return Promise.resolve({});
  }

}

export const DataConnections = new DataConnectionsResource();

